import BackupManager from '@/components/admin/backup-manager';
import DatabaseMigration from '@/components/admin/database-migration';
import SchemaComparison from '@/components/admin/schema-comparison';
import Sidebar from '@/components/layout/sidebar';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
// Firebase disabled - using WebSocket for real-time updates
// import { initFcm, unregisterFcm } from '@/lib/fcm';
import { apiRequest } from '@/lib/queryClient';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

export default function SettingsPage() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [permission, setPermission] = useState<NotificationPermission>(
    typeof Notification !== 'undefined' ? Notification.permission : 'default'
  );
  const [dismissed, setDismissed] = useState<boolean>(false);
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    if (typeof Notification !== 'undefined') {
      setPermission(Notification.permission);
    }
    setDismissed(localStorage.getItem('sb:notifications:dismissed') === 'true');
  }, []);

  const status = useMemo(() => {
    if (permission === 'granted')
      return { label: 'Enabled', variant: 'default' as const };
    if (permission === 'denied')
      return { label: 'Blocked', variant: 'destructive' as const };
    return { label: 'Not granted', variant: 'secondary' as const };
  }, [permission]);

  const handleEnable = async () => {
    // Firebase disabled - using WebSocket for real-time notifications
    setBusy(true);
    try {
      // await initFcm();
      if (typeof Notification !== 'undefined') {
        await Notification.requestPermission();
        setPermission(Notification.permission);
      }
      localStorage.removeItem('sb:notifications:dismissed');
      setDismissed(false);
      toast({
        title: 'Notifications Enabled',
        description: 'You will receive notifications via WebSocket when the app is open.',
      });
    } catch (error) {
      console.warn('Failed to enable notifications:', error);
      toast({
        title: 'Notification Setup Failed',
        description: 'Your browser may not support notifications.',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  const handleDisable = async () => {
    // Firebase disabled
    setBusy(true);
    try {
      // await unregisterFcm();
      localStorage.setItem('sb:notifications:dismissed', 'true');
      setDismissed(true);
      if (typeof Notification !== 'undefined')
        setPermission(Notification.permission);
      toast({
        title: 'Notifications Disabled',
        description: 'You will no longer receive browser notifications.',
      });
    } finally {
      setBusy(false);
    }
  };

  const resetPrompt = () => {
    localStorage.removeItem('sb:notifications:dismissed');
    setDismissed(false);
  };

  return (
    <div className="min-h-screen flex bg-gray-50">
      <Sidebar />
      <div className="flex-1 p-3 sm:p-6 space-y-4 sm:space-y-6 lg:ml-0 ml-0">
        <h2 className="text-xl sm:text-2xl font-semibold">Settings</h2>

        <Card className="p-3 sm:p-4 space-y-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex-1 min-w-0">
              <h3 className="text-base sm:text-lg font-medium">
                Notifications
              </h3>
              <p className="text-xs sm:text-sm text-muted-foreground">
                Manage browser and push notifications
              </p>
            </div>
            <Badge
              variant={status.variant}
              className="text-xs sm:text-sm w-fit"
            >
              {status.label}
            </Badge>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              onClick={handleEnable}
              disabled={busy || permission === 'granted'}
              aria-label="Enable notifications"
              className="text-xs sm:text-sm"
            >
              {busy && permission !== 'granted' ? 'Enabling…' : 'Enable'}
            </Button>
            <Button
              variant="outline"
              onClick={handleDisable}
              disabled={busy}
              aria-label="Disable notifications"
              className="text-xs sm:text-sm"
            >
              {busy && permission === 'granted' ? 'Disabling…' : 'Disable'}
            </Button>
            <Button
              variant="ghost"
              onClick={resetPrompt}
              disabled={!dismissed}
              aria-label="Reset notification prompt"
              className="text-xs sm:text-sm"
            >
              <span className="hidden sm:inline">Reset prompt</span>
              <span className="sm:hidden">Reset</span>
            </Button>
          </div>
          <ul className="text-xs sm:text-sm list-disc pl-4 sm:pl-5 text-muted-foreground space-y-1">
            <li>
              If notifications are blocked at the browser level, change the site
              permission in the address bar or browser settings.
            </li>
            <li>
              Enabling registers your device for push; disabling removes it.
            </li>
          </ul>
        </Card>

        {isAdmin && (
          <>
            <DatabaseMigration />
            <SchemaComparison />
            <BackupManager />

            <Card className="p-4 space-y-3 border-destructive">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-medium text-destructive">
                    Danger Zone
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    Clear ALL task data (tasks, history, comments, time entries,
                    help). This cannot be undone.
                  </p>
                </div>
                <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="destructive"
                      aria-label="Clear all task data"
                    >
                      Clear task data
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>
                        Are you absolutely sure?
                      </AlertDialogTitle>
                      <AlertDialogDescription>
                        This action will permanently delete ALL task-related
                        data across the system and cannot be undone. To confirm,
                        type CLEAR below and press Confirm.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-2">
                      <Input
                        placeholder="Type CLEAR to confirm"
                        value={confirmText}
                        onChange={e => setConfirmText(e.target.value)}
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        disabled={confirmText !== 'CLEAR'}
                        onClick={async () => {
                          try {
                            const result = await apiRequest(
                              'DELETE',
                              '/api/admin/tasks'
                            );
                            toast({
                              title: 'Task data cleared',
                              description: `Deleted tasks: ${result.deletedCounts?.tasks ?? 0}, time entries: ${result.deletedCounts?.timeEntries ?? 0}, comments: ${result.deletedCounts?.taskComments ?? 0}, history: ${result.deletedCounts?.taskHistory ?? 0}, help: ${result.deletedCounts?.taskHelp ?? 0}`,
                            });
                            setConfirmText('');
                            setConfirmOpen(false);
                            // Invalidate task-related queries
                            queryClient.invalidateQueries();
                            // Optional: reload to ensure full UI refresh
                            // window.location.reload();
                          } catch (error: any) {
                            toast({
                              title: 'Failed to clear task data',
                              description: error?.message ?? 'Server error',
                              variant: 'destructive',
                            });
                          }
                        }}
                      >
                        Confirm
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
